// ********************** Google Search API Call From Backend  ***********************

import { Directive, ElementRef, EventEmitter, Output, Input, OnChanges, OnDestroy, HostListener, Renderer2 } from '@angular/core';
import { UserCommonService } from '../services/user-common.service';
import { UiService } from '../services/ui.service';
import { AppSettings } from '../app.settings';
import { StorageService } from '../services/storage.service';
declare const google: any;

@Directive({
  selector: '[appSearchLocation]',
})
export class SearchLocationDirective implements OnChanges, OnDestroy {
  @Output() setAddress: EventEmitter<any> = new EventEmitter();
  @Input() onlyCity = false;
  @Input() overlayDropdown = false;
  private readonly repositionOverlay = () => this.positionOverlay();
  private searchTimer: ReturnType<typeof setTimeout>;
  private searchRequestId = 0;
  private _el: HTMLElement;
  private dropdown: HTMLElement;
  private autocompleteResults: any[] = [];
  tenantInfo: any;
  userEnteredInput: string;
  imgPath = '../../../../assets/images/temp/grey-marker.svg';

  constructor(
    el: ElementRef,
    private userCommonService: UserCommonService,
    private renderer: Renderer2,
    private uiService: UiService,
    private storageService: StorageService
  ) {
    this.tenantInfo = this.storageService.getItemFromSessionStorage(AppSettings.TENANT_INFO);
    this._el = el.nativeElement;
    this._el.setAttribute('autocomplete', 'off');
    this._el.onpaste = (e) => e.preventDefault();
    this.createDropdown();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    if (!event.isTrusted) {
      return;
    }

    const input = (event.target as HTMLInputElement).value;
    this.userEnteredInput = input;
    if (this.overlayDropdown) {
      this.queueOverlaySearch(input);
      return;
    }
    if (input.length % 3 === 0 && input) {
      this.getAutocompleteSuggestions(input);
    } else if (!input) {
      this.clearDropdown();
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: Event): void {
    const isInputFocused = this._el.contains(event.target as Node);
    const isDropdownFocused = this.dropdown.contains(event.target as Node);

    if (!isInputFocused && !isDropdownFocused) {
      this.hideDropdown();
    } else if(isInputFocused){
      this.showDropdown();
    }
  }

  private queueOverlaySearch(input: string): void {
    clearTimeout(this.searchTimer);
    this.searchRequestId += 1;
    const requestId = this.searchRequestId;
    if (!input || input.trim().length < 3) {
      if (!input) {
        this.clearDropdown();
      }
      return;
    }
    this.searchTimer = setTimeout(() => {
      if (requestId !== this.searchRequestId) {
        return;
      }
      this.getAutocompleteSuggestions(input);
    }, 300);
  }

  getAutocompleteSuggestions(input): void {
    const requestId = this.searchRequestId;
    const data = {
      input:this.userEnteredInput,
      radiusInMeter:''
    }

    this.userCommonService.getGoogleMapAutoComplete(data).subscribe(
      (response: any) => {
        if (this.overlayDropdown && requestId !== this.searchRequestId) {
          return;
        }
        const suggestions = response?.suggestions || [];
        if (suggestions.length) {
          this.autocompleteResults = suggestions;
          this.populateDropdown();
        } else if(response?.status === 'OVER_QUERY_LIMIT' && response?.error_message){
          this.uiService.showMessage(response.error_message, AppSettings.MESSAGE_TYPE.ERROR)
        } else{
          this.clearDropdown();
        }
      },
      (error) => {
        this.clearDropdown();
      }
    );
  }

  hideDropdown(){
    this.renderer.setStyle(this.dropdown, 'display', 'none');
  }

  showDropdown(){
    if (this.overlayDropdown && !this.autocompleteResults.length) {
      return;
    }
    this.renderer.setStyle(this.dropdown, 'display', 'block');
  }

  createDropdown(): void {
    this.dropdown = this.renderer.createElement('div');
    this.renderer.addClass(this.dropdown, 'places-dropdown-container');
    this.renderer.appendChild(document.body, this.dropdown);
    this.positionDropdown();
  }

  positionDropdown(): void {
    const rect = this._el.getBoundingClientRect();
    this.renderer.setStyle(this.dropdown, 'top', rect.bottom + 'px');
    this.renderer.setStyle(this.dropdown, 'left', rect.left + 'px');
  }

  populateDropdown(): void {
    if (this.overlayDropdown) {
      this.populateOverlay();
      return;
    }
    const parent = this._el.parentNode;
    const children = parent.children;
    if(children.length > 1){
      parent.removeChild(children[1]);
    }

    const parentElement = this.renderer.parentNode(this._el);
    this.renderer.setStyle(parentElement, 'position', 'relative');

    const dropdownContainer = this.renderer.createElement('div');
    this.renderer.addClass(this.dropdown, 'places-dropdown-container');

    this.renderer.insertBefore(this._el.parentNode, dropdownContainer, this._el.nextSibling);

    this.autocompleteResults.forEach((result) => {
      const placePrediction = result.placePrediction;
      const description = placePrediction?.text?.text || '';
      const placeId = placePrediction?.placeId || '';
      this.renderer.addClass(dropdownContainer, 'places-dropdown')

      this.renderer.insertBefore(this._el.parentNode, dropdownContainer, this._el.nextSibling);
  
      const options = this.renderer.createElement('div');
      const pin = this.renderer.createElement('img');
      this.renderer.setAttribute(pin, 'src', this.imgPath);
      this.renderer.addClass(pin, 'places-marker');
      this.renderer.appendChild(options, pin);
      this.renderer.setAttribute(options, 'title', result.placePrediction?.text?.text);
      this.renderer.addClass(options, 'places-dropdown-option');
      this.renderer.setStyle(options, 'width', `${this._el.offsetWidth}px`);
      this.renderer.listen(options, 'click', () => this.onOptionSelected({description,placeId}));
      const mainText = this.renderer.createText(placePrediction?.structuredFormat?.mainText?.text || description);
      const secondaryText = this.renderer.createText(placePrediction?.structuredFormat?.secondaryText?.text || '');
      const span1 = this.renderer.createElement('span');
      this.renderer.addClass(span1, 'main-text')
      const span2 = this.renderer.createElement('span');
      this.renderer.addClass(span2, 'secondary-text');
      this.renderer.appendChild(span1, mainText);
      this.renderer.appendChild(span2, secondaryText);
      this.renderer.appendChild(options, span1);
      this.renderer.appendChild(options, span2);
      this.renderer.appendChild(dropdownContainer, options);

    });

    this.dropdown = dropdownContainer;
  }

  clearDropdown(): void {
    if (this.overlayDropdown) {
      this.clearOverlay();
      return;
    }
    const parent = this._el.parentNode;
    const children = parent.children;
    if(children.length > 1){
      parent.removeChild(children[1]);
    }
  }

  private populateOverlay(): void {
    const dropdown = this.dropdown;
    while (dropdown.firstChild) {
      dropdown.removeChild(dropdown.firstChild);
    }
    this.renderer.addClass(dropdown, 'places-dropdown');
    this.renderer.setStyle(dropdown, 'position', 'fixed');
    this.renderer.setStyle(dropdown, 'z-index', '10000');
    this.renderer.setStyle(dropdown, 'display', 'block');
    this.renderer.setStyle(dropdown, 'margin-top', '0');
    this.renderer.setStyle(dropdown, 'background', '#fff');

    this.autocompleteResults.forEach((result) => {
      const placePrediction = result.placePrediction;
      const description = placePrediction?.text?.text || '';
      const placeId = placePrediction?.placeId || '';
      const options = this.renderer.createElement('div');
      const pin = this.renderer.createElement('img');
      this.renderer.setAttribute(pin, 'src', this.imgPath);
      this.renderer.addClass(pin, 'places-marker');
      this.renderer.appendChild(options, pin);
      this.renderer.setAttribute(options, 'title', description);
      this.renderer.addClass(options, 'places-dropdown-option');
      this.renderer.listen(options, 'click', () => this.onOptionSelected({ description, placeId }));
      const span1 = this.renderer.createElement('span');
      this.renderer.addClass(span1, 'main-text');
      const span2 = this.renderer.createElement('span');
      this.renderer.addClass(span2, 'secondary-text');
      this.renderer.appendChild(span1, this.renderer.createText(placePrediction?.structuredFormat?.mainText?.text || description));
      this.renderer.appendChild(span2, this.renderer.createText(placePrediction?.structuredFormat?.secondaryText?.text || ''));
      this.renderer.appendChild(options, span1);
      this.renderer.appendChild(options, span2);
      this.renderer.appendChild(dropdown, options);
    });

    this.positionOverlay();
    this.bindOverlay();
  }

  private bindOverlay(): void {
    this.unbindOverlay();
    document.addEventListener('scroll', this.repositionOverlay, true);
    window.addEventListener('resize', this.repositionOverlay);
  }

  private unbindOverlay(): void {
    document.removeEventListener('scroll', this.repositionOverlay, true);
    window.removeEventListener('resize', this.repositionOverlay);
  }

  private positionOverlay(): void {
    if (!this.dropdown || !this._el) {
      return;
    }
    const rect = this._el.getBoundingClientRect();
    this.renderer.setStyle(this.dropdown, 'top', `${rect.bottom + 4}px`);
    this.renderer.setStyle(this.dropdown, 'left', `${rect.left}px`);
    this.renderer.setStyle(this.dropdown, 'width', `${rect.width}px`);
  }

  private clearOverlay(): void {
    this.unbindOverlay();
    this.autocompleteResults = [];
    if (!this.dropdown) {
      return;
    }
    while (this.dropdown.firstChild) {
      this.dropdown.removeChild(this.dropdown.firstChild);
    }
    this.renderer.setStyle(this.dropdown, 'display', 'none');
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimer);
    this.searchRequestId += 1;
    this.unbindOverlay();
    this.dropdown?.remove();
  }

  onOptionSelected(option: any): void {
    this.userCommonService.getGoogleMapLatLng(option.placeId).subscribe(response => {
      const location = response?.results?.[0]?.geometry?.location;
      const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
      const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;
      const placeObj = {
        name: option.description,
        address: option.description,
        lat,
        lng,
        id: this._el.id,
        placeId: option.placeId || response?.results?.[0]?.place_id || '',
        postalCode: this.getZipCode(response?.results?.[0])
      };
      this.invokeEvent(placeObj);
      (this._el as HTMLInputElement).value = option.description;
      this.clearDropdown();
    });
  }

  getZipCode(result): string {
    let zipCode = '';
    for (const component of result?.address_components || []) {
      if (component.types.includes('postal_code')) {
        zipCode = component.short_name;
      }
    }
    return zipCode;
  }

  ngOnChanges() {
    if (this.overlayDropdown && this.dropdown && !this.autocompleteResults.length) {
      this.renderer.setStyle(this.dropdown, 'display', 'none');
    }
  }

  invokeEvent(place: object) {
    this.setAddress.emit(place);
  }
}

// ************************** Google Search API Call From FrontEnd  ***********************************

